/**
 * Per-section readiness predicates for the confirm flow
 * (SPEC-SOR-174 R2/R6/R7/R8/R9/R26/R31).
 *
 * Readiness answers exactly one question — "is this section ready to be
 * confirmed" — and nothing else. No predicate here confirms a section, writes a
 * status, or disables a control: R28 makes the filer's own confirm act the sole
 * provenance of `confirmed`, so a ready section is still unconfirmed until the
 * filer says so. The gates that consume these verdicts are a separate concern.
 *
 * Every predicate is a BICONDITIONAL of its requirement, not a floor. Tightening
 * or loosening one at a call site re-opens the gap between the readiness the
 * confirm control offers (R31) and the readiness R3 demands of an already
 * confirmed section.
 *
 * Like `guided-sections.ts` this module takes NARROW structural slices of the
 * wizard state rather than `EFilingWizardState`, so each predicate is callable
 * (and testable) with only the fields it actually reads. `EFilingWizardState`
 * satisfies every slice structurally. The `.js` extensions on the relative
 * imports are the Docker-build ESM rule, not an optional style.
 */
import { caseCourtBinding } from '../common/court-identity.js';
import { validateParties, collectDocSelfRepPartyIds } from '../common/parties.js';
import { getSectionStatus, predecessorsConfirmed, courtSectionConfirmable, } from './guided-sections.js';
import { getAssignmentProvenance, PARTY_CLASS_CODES, } from './assignment-provenance.js';
/** The filing-type values the case section switches on (`FilingType`). */
const SUBSEQUENT_FILING = 'subsequent-filing';
const CASE_INITIATION = 'case-initiation';
/**
 * R6/R7/R26 — the case section, whose meaning is chosen by the filing type. The
 * three `FilingType` members are each pinned, so the predicate is TOTAL: there
 * is no member a reader can take as confirmable by default.
 *
 * `subsequent_filing` (R6): a case is attached AND its recorded CaseCourtBinding
 * does not place the case at another court. The comparison runs through
 * `caseCourtBinding` — the repo's one canonical court-identity comparator —
 * never raw string equality: `orderInfo.county` is a county DISPLAY name
 * ("Madera") while the binding is the slug the case record carries ("madera"),
 * so `!==` reports a same-court filing unready. An ABSENT binding classifies
 * `absent`, never `matches-court`, so it leaves the section unready rather than
 * grace-passing — and since SOR-191 "absent" includes an UNATTRIBUTABLE binding
 * (a pre-SOR-182 filing-county echo), so a legacy draft is unready until the
 * case is re-attached and the record asserts its own court.
 *
 * `unrecognized-court` is READY, and that asymmetry against `absent` is the
 * point. `absent` says the draft carries no court for its case and the filer can
 * fix it by re-running the lookup; `unrecognized-court` says the case record DID
 * assert a court and we cannot place its value space (see the value-space note
 * on `caseCourtBinding`), which no filer act can repair. Refusing it would leave
 * every subsequent filing under such a court permanently unconfirmable with no
 * remedy on any step — the deadlock R21 exists to make unreachable — so the
 * unplaceable verdict keeps the pre-SOR-182 behaviour instead.
 *
 * `case_initiation` (R7): the category and type both resolve against the
 * selected court's taxonomy. `caseCategoryCode` is the authoritative resolved
 * signal and carries the "not cleared as foreign" clause on its own — the
 * SOR-169 recovery clears the code and parks the reading in
 * `caseCategoryCandidates` / `ocrExtractedCategoryName`, so a category surviving
 * only as a candidate has an empty code by construction. Testing the candidates
 * list directly would be wrong in the other direction: it is retained (merely
 * ignored) once a code resolves, so its presence must not un-ready a settled
 * category.
 *
 * `unset` (R26): neither meaning applies before the filing type is chosen.
 */
export function caseSectionConfirmable(state) {
    const { orderInfo, caseInfo } = state;
    switch (orderInfo.filingType) {
        case SUBSEQUENT_FILING: {
            if (!caseInfo.caseTrackingId)
                return false;
            const binding = caseCourtBinding(caseInfo, orderInfo.county);
            return binding === 'matches-court' || binding === 'unrecognized-court';
        }
        case CASE_INITIATION:
            return Boolean(caseInfo.caseCategoryCode) && Boolean(caseInfo.caseType?.code);
        default:
            return false;
    }
}
/**
 * R8 — the parties section: the roster satisfies party validation and, on a
 * subsequent filing, the filing USES the court roster.
 *
 * The validation leg delegates wholly to the shared `validateParties`, which
 * already fails an empty roster on `min-parties`. Re-deriving a `length > 0`
 * guard here would be a second implementation of that rule, free to drift from
 * the one the filer's error banner reads.
 *
 * The court-roster leg is about what the filing REFERENCES, not about what the
 * roster contains, and that distinction is the whole rule. Stating it as roster
 * PURITY ("every entry is `case-roster`") makes the section permanently
 * unconfirmable on any subsequent filing that has ever run an extraction:
 * `mergeExtractedParties`/`mergeExtractedAttorneys` append genuinely-new
 * entries tagged `extraction` by design (SOR-223), `applyExtraction` runs them
 * unguarded by filing type, and the wizard documents extraction-after-attach as
 * a supported path — so a single appended entry would refuse `sec_parties`
 * forever, taking its successor `sec_documents` and Submit with it, with no act
 * available to the filer that clears it. It would also refuse the filings that
 * legitimately need a non-court-record participant at all: a Substitution of
 * Attorney names an INCOMING attorney the court record cannot already hold.
 *
 * What the court roster's authority actually means here is R87's rule: on a
 * subsequent filing the existing-data selectors never OFFER an extraction-
 * sourced entry, so what a document may be filed on behalf of is the court's
 * own roster. This leg pins that closure — no party-class reference on any
 * document resolves to an extraction-sourced entry — which is decidable from
 * the draft alone and always repairable in place: the fix is to re-pick the
 * participant from the selector, which offers exactly the court entries. An
 * entry merely PRESENT and referenced by nothing changes no filing and is
 * therefore not the section's business. `manual` entries are untouched by this
 * leg in either direction (an untagged legacy entry reads `manual` too), since
 * the Case Participants flow is how a filer adds the participant a subsequent
 * filing needs and the court record does not have.
 */
export function partiesSectionConfirmable(state) {
    const result = validateParties({
        parties: state.parties,
        attorneys: state.attorneys,
        docSelfRepPartyIds: collectDocSelfRepPartyIds(state.documents),
    });
    if (!result.valid)
        return false;
    if (state.orderInfo.filingType !== SUBSEQUENT_FILING)
        return true;
    return !partiesReferenceExtractionEntry(state);
}
/**
 * R8's court-roster leg on its own — true when some document is filed on behalf
 * of an extraction-sourced participant. Exported so `sectionGateMessage` can
 * tell the filer which of the two party faults it is (an EXTRA reference reads
 * nothing like a missing entry) without re-deriving the predicate.
 */
export function partiesReferenceExtractionEntry(state) {
    return collectExtractionSourcedReferences(state).size > 0;
}
/**
 * The ids of extraction-sourced roster entries that some document REFERENCES.
 * Empty on every draft that files only on court-roster (or filer-entered)
 * participants — including the common one where an extraction appended entries
 * that nothing points at.
 *
 * The reference shape is the same all-strings-array guard `referencesResolve`
 * and `remapMetadataReferences` use, for the same reason: only existing-data
 * references persist as string arrays of local roster ids, while a `codeList`
 * value is a scalar string and a `new-data` payload (NEW_ATTORNEY, NEW_*_PARTY)
 * an object array. A Substitution's incoming attorney therefore rides in
 * NEW_ATTORNEY and is invisible to this scan by construction, which is what
 * keeps that filing confirmable.
 *
 * Party and attorney ids share one set deliberately: a reference naming an
 * extraction-sourced entry is out of bounds whichever roster it came from, and
 * classifying the FIELD would need the court's policy, which readiness must
 * stay total without.
 */
function collectExtractionSourcedReferences(state) {
    const extractionIds = new Set([
        ...state.parties.filter(p => p.source === 'extraction').map(p => p.id),
        ...state.attorneys.filter(a => a.source === 'extraction').map(a => a.id),
    ]);
    const referenced = new Set();
    if (extractionIds.size === 0)
        return referenced;
    for (const doc of state.documents) {
        for (const value of Object.values(doc.metadataValues ?? {})) {
            if (!Array.isArray(value))
                continue;
            if (!value.every((v) => typeof v === 'string'))
                continue;
            for (const id of value) {
                if (extractionIds.has(id))
                    referenced.add(id);
            }
        }
    }
    return referenced;
}
/**
 * True when `value` carries no unresolvable reference into `roster`.
 *
 * Only existing-data references persist as string ARRAYS of local roster ids —
 * the identical guard `remapMetadataReferences` uses to pick them out. A
 * `codeList` value persists as a scalar string and a `new-data` payload
 * (NEW_ATTORNEY, NEW_*_PARTY) as an object array, so neither reaches the roster
 * check. A non-array value is likewise not a reference the envelope would emit:
 * `getPartyIds` unwraps `string[]` and silently ignores every other shape.
 *
 * A document carrying NO reference resolves vacuously (AX6) — R9 asks whether
 * the references that exist point at current entries, not whether they exist.
 * Requiring them is `validateDocumentMetadata`'s job, off the court's policy.
 */
function referencesResolve(value, roster) {
    if (!Array.isArray(value))
        return true;
    if (!value.every((v) => typeof v === 'string'))
        return true;
    return value.every(id => roster.has(id));
}
/**
 * R9 — the documents section: at least one document, every document typed from
 * the court and case scoped list, and every party-class reference on it
 * resolving to a current roster entry.
 *
 * The non-emptiness conjunct is explicit and load-bearing. Without it the
 * universal over documents is satisfied by the zero-document draft, so a
 * documents section could confirm — and a filing reach Submit — with the
 * court-scoped type bar never positively exercised. Empty is unready, never
 * vacuously ready.
 */
/**
 * The party-class codes a document type's policy offers a control for —
 * exactly the `existing-data` participant/assignment items the section
 * renders. Only these can carry a payable `cleared` demand.
 */
function exposedPartyClassCodes(items) {
    const out = new Set();
    for (const item of items) {
        if (item.valueRestriction !== 'existing-data')
            continue;
        if (item.classType !== 'caseParticipant' && item.classType !== 'caseAssignment')
            continue;
        out.add(item.code);
    }
    return out;
}
/**
 * True when the document's chosen filing party is self-represented — by the
 * doc-level SELF_REP flag or by every referenced party's own flag — the SAME
 * predicate the envelope's stage-3 `isSelfRep` and the shared validator read
 * (Walter 2026-07-10 R18/R19). A document with no filing party chosen is not
 * self-represented: the attorney slot's demand stands until the party does.
 */
function filingPartySelfRepresented(values, parties) {
    if (values.SELF_REP === true || values.SELF_REP === 'true')
        return true;
    const ids = values.FILING_PARTY;
    if (!Array.isArray(ids) || ids.length === 0)
        return false;
    if (!ids.every((v) => typeof v === 'string'))
        return false;
    return ids.every(id => parties.find(p => p.id === id)?.isSelfRepresented === true);
}
export function documentsSectionConfirmable(state, options) {
    const { documents } = state;
    if (documents.length === 0)
        return false;
    const allowedTypes = options?.availableDocTypeCodes
        ? new Set(options.availableDocTypeCodes)
        : null;
    const partyIds = new Set(state.parties.map(p => p.id));
    const attorneyIds = new Set(state.attorneys.map(a => a.id));
    for (const doc of documents) {
        const typeCode = doc.documentType?.code;
        if (!typeCode)
            return false;
        if (allowedTypes && !allowedTypes.has(typeCode))
            return false;
        const values = doc.metadataValues ?? {};
        const items = options?.metadataByDocType?.[typeCode];
        // A slot reading `cleared` carries a demand for re-choice, not a symptom
        // of a dangling reference — the case-change writer may have stamped it
        // while dropping the orphaned reference, so the tag fires on its own.
        // Read through the getter: it is the sole reader that resolves absence.
        //
        // The demand is only payable through a control the filer is offered
        // (the WOL-419 no-deadlock rule: a gate may only reference fields editable
        // where it fires). With the court's policy in hand, a cleared slot whose
        // code the policy exposes as no existing-data control — a seed the
        // gap-fill once populated, say REFERS_TO_PARTIES on a type that never asks
        // for it — is moot: nothing on screen could ever re-arm it. Without policy
        // every cleared slot refuses, as before (the R7 witness's other regimes).
        //
        // FILING_ATTORNEY is the other payable-by-construction case: when every
        // filing party the filer chose is self-represented there is no attorney to
        // choose, and R18/R19 already treat the self-rep party as satisfying the
        // attorney slot — so a cleared attorney slot behind a self-rep filing
        // party demands nothing the filer can supply.
        const exposed = items ? exposedPartyClassCodes(items) : null;
        for (const code of PARTY_CLASS_CODES) {
            if (getAssignmentProvenance(doc, code) !== 'cleared')
                continue;
            if (exposed && !exposed.has(code))
                continue;
            if (code === 'FILING_ATTORNEY' && filingPartySelfRepresented(values, state.parties))
                continue;
            return false;
        }
        if (items) {
            // Policy in hand: classify each reference field the way the shipped
            // DocumentsTab reconciler does, so readiness agrees with the drop-only
            // backstop instead of approximating it.
            for (const item of items) {
                if (item.valueRestriction !== 'existing-data')
                    continue;
                const roster = item.classType === 'caseAssignment'
                    ? attorneyIds
                    : item.classType === 'caseParticipant'
                        ? partyIds
                        : null;
                if (!roster)
                    continue;
                if (!referencesResolve(values[item.code], roster))
                    return false;
            }
            continue;
        }
        // No policy: every reference array is checked against the combined roster.
        // Looser than the classified path by exactly one case — an attorney id
        // sitting in a party-class field still resolves — and that is the correct
        // direction for a fallback, which must not un-ready a draft over reference
        // data it cannot classify.
        const rosterIds = new Set([...partyIds, ...attorneyIds]);
        for (const value of Object.values(values)) {
            if (!referencesResolve(value, rosterIds))
                return false;
        }
    }
    return true;
}
/**
 * The readiness dispatcher: a section's own predicate, and nothing about the
 * ladder or the filer's acts. Total over `GuidedSection`; an unrecognized
 * section is unready rather than vacuously ready, mirroring
 * `predecessorsConfirmed`'s treatment of a section with no place in the order.
 */
export function confirmable(state, section, options) {
    switch (section) {
        case 'sec_court':
            return courtSectionConfirmable(state);
        case 'sec_case':
            return caseSectionConfirmable(state);
        case 'sec_parties':
            return partiesSectionConfirmable(state);
        case 'sec_documents':
            return documentsSectionConfirmable(state, options);
        default:
            return false;
    }
}
/**
 * R2 + R31 — whether the wizard offers a section's confirm control. The two
 * requirements have disjoint antecedents and between them determine the offer
 * everywhere except on an already-confirmed section, so one conjunction states
 * both halves: offered exactly when the section is READY, every strictly
 * earlier section is confirmed, and it is not already confirmed.
 *
 * The positive half (R31) is why extraction can never become a hard gate on the
 * filing. Readiness is computed from the draft's own data, so a filer whose
 * extraction read nothing — or read wrongly — is offered each control the moment
 * that section is ready on hand-entered data. Nothing here consults
 * `ocrPopulatedFields`, `ocrConfirmedFields`, or the `prefilled` status:
 * pre-fill accelerates the offer and is never a precondition of it. A
 * `pending` section that is ready is offered exactly as a `prefilled` one is.
 */
export function confirmOffered(state, section, options) {
    if (getSectionStatus(state, section) === 'confirmed')
        return false;
    return confirmable(state, section, options) && predecessorsConfirmed(state, section);
}
